import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import PulseLoader from '@/components/PulseLoader';
import { authService } from '../../utils/authService';
import { fetchBatchesByCourseSingle } from '../../utils/batchUtils';
import EditSessionModal from '../../LMS/TR/files/sessionUtils/EditSessionModal';
import type { Session } from '../../LMS/TR/files/sessionUtils/sessionTypes';
import ConfirmationModal from '../../components/ConfirmationModal';

type UnassignedSession = {
  session_id: string;
  session_title: string;
  session_status: string;
  session_type?: string;
  trainer_name: string;
  session_assigned_to?: string;
  session_subject?: string;
  track_id: string;
  course_id: string;
  batch_id?: string | string[];
  needs_batch?: boolean;
  needs_students?: boolean;
  start_date: string;
  start_time: string;
  end_date?: string;
  end_time?: string;
  live_session_category: string;
};

const FINISHED_STATUSES = new Set(['cancelled', 'completed', 'ended']);

/** Navigation state from workshop Confirm & Create → Add batches now. */
type AutoAssignNavState = {
  autoAssignSessionId?: string;
  sessionTitle?: string;
  trackId?: string;
  courseId?: string;
  liveSessionCategory?: string;
  startDate?: string;
  startTime?: string;
  fromWorkshopCreate?: boolean;
};

type Batch = { batch_id: string; batch_name: string };
type StudentRow = {
  stuId: string;
  stuname?: string;
  email?: string;
  collegeName?: string;
};

const CATEGORY_LABELS: Record<string, string> = {
  mandatory: 'Live Class Mandatory',
  optional: 'Live Class Optional',
  workshop_online: 'Online Workshop',
  workshop_onsite: 'Onsite Workshop',
  workshop: 'Workshop',
};

function formatStart(session: UnassignedSession): string {
  if (!session.start_date) return '—';
  const time = session.start_time || '';
  return time ? `${session.start_date} · ${time}` : session.start_date;
}

function inviteTypeLabel(sessionType?: string): string {
  const t = String(sessionType || 'group').toLowerCase();
  return t === 'personal' ? 'Personal' : 'Group';
}

function categoryLabel(category?: string): string {
  const key = String(category || '').trim().toLowerCase();
  if (!key) return '—';
  return CATEGORY_LABELS[key] || key.replace(/_/g, ' ');
}

function isPersonalSession(session: UnassignedSession | null): boolean {
  return String(session?.session_type || '').toLowerCase() === 'personal';
}

function assignActionLabel(session: UnassignedSession): string {
  if (isPersonalSession(session)) return 'Assign students';
  if (session.needs_batch === false && session.needs_students !== false) {
    return 'Assign students';
  }
  return 'Assign batch';
}

function assignActionIcon(session: UnassignedSession): React.ReactElement {
  if (isPersonalSession(session)) return <PersonAddOutlinedIcon fontSize="small" />;
  if (session.needs_batch === false && session.needs_students !== false) {
    return <PersonAddOutlinedIcon fontSize="small" />;
  }
  return <GroupAddOutlinedIcon fontSize="small" />;
}

const iconButtonSx = {
  borderRadius: '8px',
  width: 32,
  height: 32,
};

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_ROWS_PER_PAGE = 10;

function normalizeBatchIds(value: string | string[] | undefined | null): string[] {
  if (Array.isArray(value)) {
    return value.map((id) => String(id || '').trim()).filter(Boolean);
  }
  const single = String(value || '').trim();
  return single ? [single] : [];
}

function mergeStudentsById(rows: StudentRow[]): StudentRow[] {
  const byId = new Map<string, StudentRow>();
  for (const row of rows) {
    const id = String(row?.stuId || '').trim();
    if (!id || byId.has(id)) continue;
    byId.set(id, row);
  }
  return Array.from(byId.values());
}

function isFinishedSessionStatus(status?: string): boolean {
  return FINISHED_STATUSES.has(String(status || '').trim().toLowerCase());
}

function canEditSession(session: UnassignedSession): boolean {
  return !isFinishedSessionStatus(session.session_status);
}

function canCancelSession(session: UnassignedSession): boolean {
  return String(session.session_status || '').trim().toLowerCase() !== 'cancelled';
}

function unassignedToEditSession(row: UnassignedSession): Session {
  const sessionId = Number.parseInt(String(row.session_id || ''), 10);
  return {
    id: Number.isFinite(sessionId) ? sessionId : 0,
    Session_Topic: row.session_title || '',
    Date: row.start_date || '',
    Start_Time: row.start_time || '',
    End_Time_date: row.end_date || row.start_date || '',
    End_Time_time: row.end_time || '',
    Expected_End_Time_date: row.end_date || row.start_date || '',
    Expected_End_Time_time: row.end_time || '',
    meetlink: '',
    conductedby: row.trainer_name || '',
    conductedby_email: '',
    session_assigned_to: row.session_assigned_to || '',
    subject: row.session_subject || '',
    co_trainers: [],
    attendances: { invited_students_count: 0, attended_students_count: 0 },
    session_status: row.session_status || 'scheduled',
    session_type: row.session_type,
    live_session_category: row.live_session_category,
    track_id: row.track_id || '',
    course_id: row.course_id || '',
    batch_id: row.batch_id || '',
  };
}

export default function AssignSessions() {
  const location = useLocation();
  const navigate = useNavigate();
  const autoAssignHandledRef = useRef(false);

  const [sessions, setSessions] = useState<UnassignedSession[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [active, setActive] = useState<UnassignedSession | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  /** Students already invited this assign session (kept selected at bottom). */
  const [alreadyAssignedIds, setAlreadyAssignedIds] = useState<string[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignConflicts, setAssignConflicts] = useState<
    Array<{
      studentId: string;
      studentName: string;
      conflicts: Array<{
        sessionId: string;
        sessionTitle: string;
        sessionStart: string;
        sessionEnd: string;
        trainer?: string;
      }>;
    }>
  >([]);
  /** After a successful assign — ask before dropping the row from the list. */
  const [continuePromptOpen, setContinuePromptOpen] = useState(false);
  const [lastAssignSummary, setLastAssignSummary] = useState<string>('');
  const [editOpen, setEditOpen] = useState(false);
  const [editSession, setEditSession] = useState<Session | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<UnassignedSession | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const personal = isPersonalSession(active);
  const batchIdsKey = batchIds.join(',');
  const alreadyAssignedKey = alreadyAssignedIds.join(',');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.post('/session/codelive/unassigned/', {
        page: page + 1,
        page_size: rowsPerPage,
      });
      if (response?.data?.success) {
        setSessions(response.data.data || []);
        setTotalCount(Number(response.data.total) || 0);
      } else {
        setSessions([]);
        setTotalCount(0);
        setError(response?.data?.message || 'Failed to load unassigned sessions');
      }
    } catch (err) {
      console.error(err);
      setSessions([]);
      setTotalCount(0);
      setError('Failed to load unassigned sessions');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [totalCount, rowsPerPage, page]);

  const openAssign = useCallback((session: UnassignedSession) => {
    setActive(session);
    setAssignError(null);
    setAssignConflicts([]);
    setBatchIds(normalizeBatchIds(session.batch_id));
    setBatches([]);
    setStudents([]);
    setSelectedStudentIds([]);
    setAlreadyAssignedIds([]);
    setContinuePromptOpen(false);
    setLastAssignSummary('');
    setAssignOpen(true);
  }, []);

  const openEdit = useCallback((session: UnassignedSession) => {
    setEditSession(unassignedToEditSession(session));
    setEditOpen(true);
  }, []);

  const requestCancelSession = useCallback((session: UnassignedSession) => {
    setCancelTarget(session);
  }, []);

  const performCancelSession = useCallback(async () => {
    if (!cancelTarget) return;
    const session = cancelTarget;
    setCancelTarget(null);

    setCancellingId(session.session_id);
    try {
      await authService.put('/session/update/', {
        session_id: session.session_id,
        session_title: '',
        session_subject: '',
        session_meetlink: '',
        start_date: '',
        start_time: '',
        end_date: '',
        end_time: '',
        session_assigned_to: '',
        session_status: 'cancelled',
        track_id: [],
        course_id: [],
        batch_id: [],
        subject_id: [],
      });
      if (active?.session_id === session.session_id) {
        setAssignOpen(false);
        setActive(null);
      }
      await loadSessions();
    } catch (err) {
      console.error(err);
      setCancelError('Failed to cancel session. Please try again.');
    } finally {
      setCancellingId(null);
    }
  }, [active?.session_id, cancelTarget, loadSessions]);

  // After workshop create → "Add batches now": open assign dialog for that session.
  useEffect(() => {
    if (loading || autoAssignHandledRef.current) return;
    const nav = (location.state || null) as AutoAssignNavState | null;
    const sessionId = String(nav?.autoAssignSessionId || '').trim();
    if (!sessionId) return;

    autoAssignHandledRef.current = true;
    const found = sessions.find(
      (s) => String(s.session_id) === sessionId,
    );
    if (found) {
      openAssign(found);
    } else if (nav?.courseId) {
      openAssign({
        session_id: sessionId,
        session_title: nav.sessionTitle || 'Workshop session',
        session_status: 'scheduled',
        session_type: 'group',
        trainer_name: '',
        track_id: nav.trackId || '',
        course_id: nav.courseId,
        needs_batch: true,
        start_date: nav.startDate || '',
        start_time: nav.startTime || '',
        live_session_category: nav.liveSessionCategory || 'workshop_online',
      });
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [loading, sessions, location.state, location.pathname, navigate, openAssign]);

  const finishAssignAndLeaveList = async () => {
    setContinuePromptOpen(false);
    setAssignOpen(false);
    setActive(null);
    setLastAssignSummary('');
    setAlreadyAssignedIds([]);
    await loadSessions();
  };

  const continueAssigning = () => {
    // Keep session on the Assign list + reopen picker for more invites.
    setContinuePromptOpen(false);
    setAssignError(null);
    setAssignConflicts([]);
    setAssignOpen(true);
  };

  useEffect(() => {
    if (!assignOpen || !active?.course_id) {
      setBatches([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingBatches(true);
      try {
        const data = await fetchBatchesByCourseSingle(active.course_id);
        if (!cancelled) setBatches(data || []);
      } catch (err) {
        console.error(err);
        if (!cancelled) setBatches([]);
      } finally {
        if (!cancelled) setLoadingBatches(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignOpen, active?.course_id]);

  useEffect(() => {
    if (!assignOpen || !personal || !active?.course_id || batchIds.length === 0) {
      setStudents([]);
      if (!alreadyAssignedIds.length) setSelectedStudentIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingStudents(true);
      try {
        const results = await Promise.all(
          batchIds.map(async (batchId) => {
            const url = `/student/getStudents/session-filtered/?course_id=${encodeURIComponent(
              active.course_id,
            )}&batch_id=${encodeURIComponent(batchId)}`;
            const response = await authService.get(url);
            if (Array.isArray(response?.data)) return response.data as StudentRow[];
            if (Array.isArray(response?.data?.students)) {
              return response.data.students as StudentRow[];
            }
            return [] as StudentRow[];
          }),
        );
        if (!cancelled) {
          const merged = mergeStudentsById(results.flat());
          const assignedSet = new Set(alreadyAssignedIds.map(String));
          // Unassigned first, already-assigned at the end.
          const sorted = [
            ...merged.filter((s) => !assignedSet.has(String(s.stuId))),
            ...merged.filter((s) => assignedSet.has(String(s.stuId))),
          ];
          setStudents(sorted);
          // Keep already-assigned checked; clear only new picks on reload.
          setSelectedStudentIds(
            sorted
              .map((s) => String(s.stuId))
              .filter((id) => assignedSet.has(id)),
          );
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setStudents([]);
          setSelectedStudentIds(
            alreadyAssignedIds.length ? [...alreadyAssignedIds] : [],
          );
        }
      } finally {
        if (!cancelled) setLoadingStudents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    assignOpen,
    personal,
    active?.course_id,
    batchIdsKey,
    alreadyAssignedKey,
  ]);

  const alreadyAssignedSet = useMemo(
    () => new Set(alreadyAssignedIds.map(String)),
    [alreadyAssignedKey],
  );

  const allStudentIds = useMemo(
    () => students.map((s) => String(s.stuId)),
    [students],
  );
  const newStudentIds = useMemo(
    () => allStudentIds.filter((id) => !alreadyAssignedSet.has(id)),
    [allStudentIds, alreadyAssignedSet],
  );
  const newlySelectedIds = useMemo(
    () => selectedStudentIds.filter((id) => !alreadyAssignedSet.has(id)),
    [selectedStudentIds, alreadyAssignedSet],
  );
  const allSelected =
    newStudentIds.length > 0 &&
    newStudentIds.every((id) => selectedStudentIds.includes(id));

  const canSave = useMemo(() => {
    if (!active || batchIds.length === 0 || saving) return false;
    if (!active.track_id || !active.course_id) return false;
    // Personal: need at least one student not already invited.
    if (personal) return newlySelectedIds.length > 0;
    return true;
  }, [
    active,
    batchIds.length,
    saving,
    personal,
    newlySelectedIds.length,
  ]);

  const batchLabelById = useMemo(() => {
    const map = new Map(batches.map((b) => [b.batch_id, b.batch_name]));
    return map;
  }, [batches]);

  const handleBatchChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setBatchIds(typeof value === 'string' ? value.split(',') : value);
  };

  const toggleStudent = (stuId: string) => {
    // Already invited — stay selected at the bottom.
    if (alreadyAssignedSet.has(stuId)) return;
    setSelectedStudentIds((prev) =>
      prev.includes(stuId) ? prev.filter((id) => id !== stuId) : [...prev, stuId],
    );
  };

  const toggleSelectAll = () => {
    setSelectedStudentIds(
      allSelected
        ? [...alreadyAssignedIds]
        : Array.from(new Set([...alreadyAssignedIds, ...newStudentIds])),
    );
  };

  const handleAssign = async () => {
    if (!active || !canSave) return;
    setSaving(true);
    setAssignError(null);
    setAssignConflicts([]);
    try {
      const payload: Record<string, unknown> = {
        session_id: active.session_id,
        track_id: active.track_id,
        course_id: active.course_id,
        batch_id: batchIds,
        // Personal: invite only the selected students (never whole batch).
        // Group: invite everyone in the selected batch(es).
        auto_invite_students: true,
      };
      if (personal) {
        if (newlySelectedIds.length === 0) {
          setAssignError('Select at least one new student for a personal session.');
          setSaving(false);
          return;
        }
        // Only invite newly selected — already-assigned stay checked in the UI.
        payload.student_ids = newlySelectedIds;
      }
      const response = await authService.put('/session/codelive/assign-batch/', payload);
      if (!response?.data?.success) {
        const rawConflicts = response?.data?.sessions_by_student;
        const parsed: typeof assignConflicts = [];
        if (rawConflicts && typeof rawConflicts === 'object') {
          for (const [studentId, info] of Object.entries(
            rawConflicts as Record<string, any>,
          )) {
            const row = info && typeof info === 'object' ? info : {};
            const conflictsRaw = Array.isArray(row.conflicts)
              ? row.conflicts
              : Array.isArray(info)
                ? info
                : [];
            parsed.push({
              studentId: String(row.student_id || studentId),
              studentName: String(row.student_name || studentId),
              conflicts: conflictsRaw.map((c: any) => ({
                sessionId: String(c?.session_id || ''),
                sessionTitle: String(c?.session_title || 'Untitled session'),
                sessionStart: String(c?.session_start || c?.session_starttime || ''),
                sessionEnd: String(c?.session_end || c?.session_expected_endtime || ''),
                trainer: String(c?.session_assigned_to || ''),
              })),
            });
          }
        }
        setAssignConflicts(parsed);
        setAssignError(
          response?.data?.message ||
            response?.data?.invite_message ||
            'Failed to assign session',
        );
        return;
      }
      const invited = Number(response?.data?.students_invited || 0);
      if (personal) {
        const n = newlySelectedIds.length || invited;
        setAlreadyAssignedIds((prev) =>
          Array.from(
            new Set([...prev.map(String), ...newlySelectedIds.map(String)]),
          ),
        );
        setLastAssignSummary(
          n === 1
            ? '1 student was assigned to this session.'
            : `${n} students were assigned to this session.`,
        );
      } else {
        const n = batchIds.length;
        setLastAssignSummary(
          n === 1
            ? '1 batch was assigned to this session.'
            : `${n} batches were assigned to this session.`,
        );
      }
      // Don't reload yet — session would leave the list once anyone is invited.
      setAssignOpen(false);
      setContinuePromptOpen(true);
    } catch (err: any) {
      console.error(err);
      setAssignError(
        err?.response?.data?.detail || err?.message || 'Failed to assign session',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      className="bg-white me-1 rounded-2"
      sx={{
        height: 'calc(100vh - 90px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#0f172a' }}>
          Assign Sessions
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 13 }}>
          Sessions needing batch or student assignment ({totalCount})
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshOutlinedIcon fontSize="small" />}
            onClick={() => void loadSessions()}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <PulseLoader color="#0d6efd" size={10} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : totalCount === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            color: '#64748b',
            border: '1px dashed #cbd5e1',
            borderRadius: 2,
          }}
        >
          No sessions need assignment. Live Class, Optional, and Workshop
          sessions without a batch or students appear here.
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e5e7eb',
            borderRadius: 1.5,
          }}
        >
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Session</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Trainer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Invite</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session) => (
                <TableRow key={session.session_id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {session.session_title || `Session ${session.session_id}`}
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: 12 }}>
                      #{session.session_id} · {session.session_status || 'scheduled'}
                    </Typography>
                  </TableCell>
                  <TableCell>{session.trainer_name || '—'}</TableCell>
                  <TableCell>{inviteTypeLabel(session.session_type)}</TableCell>
                  <TableCell>{categoryLabel(session.live_session_category)}</TableCell>
                  <TableCell>{formatStart(session)}</TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 0.75,
                      }}
                    >
                      {canEditSession(session) ? (
                        <Tooltip title="Edit session">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(session)}
                            sx={{
                              ...iconButtonSx,
                              border: '1px solid #cbd5e1',
                              color: '#2563eb',
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {canCancelSession(session) ? (
                        <Tooltip
                          title={
                            cancellingId === session.session_id
                              ? 'Cancelling…'
                              : 'Cancel session'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={cancellingId === session.session_id}
                              onClick={() => requestCancelSession(session)}
                              sx={{
                                ...iconButtonSx,
                                border: '1px solid #fca5a5',
                              }}
                            >
                              {cancellingId === session.session_id ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <CancelOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      <Tooltip title={assignActionLabel(session)}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => openAssign(session)}
                            disabled={isFinishedSessionStatus(session.session_status)}
                            sx={{
                              ...iconButtonSx,
                              bgcolor: '#2563eb',
                              color: '#fff',
                              '&:hover': { bgcolor: '#1d4ed8' },
                              '&.Mui-disabled': {
                                bgcolor: '#93c5fd',
                                color: '#fff',
                              },
                            }}
                          >
                            {assignActionIcon(session)}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rows per page:"
            disabled={loading}
            sx={{
              flexShrink: 0,
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#fafafa',
              px: 1,
              '& .MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '12px',
                color: '#666',
                mb: 0,
              },
              '& .MuiTablePagination-actions': { ml: 1 },
              '& .MuiIconButton-root': { p: '6px' },
              '& .Mui-disabled': { opacity: 0.45 },
            }}
          />
        </Box>
      )}

      <Dialog
        open={continuePromptOpen}
        disableEscapeKeyDown
        onClose={() => {
          /* Must pick Yes / No — avoid dropping the row without an explicit choice. */
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Continue assigning?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#334155', mb: 1 }}>
            {lastAssignSummary || 'Assignment saved.'}
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>
            {personal
              ? 'Do you want to continue adding students to this session?'
              : 'Do you want to continue assigning batches to this session?'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: 1 }}>
            Choose No to finish — this session will leave the Assign Sessions list
            if it no longer needs assignment.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => void finishAssignAndLeaveList()}
            sx={{ textTransform: 'none' }}
          >
            No, I&apos;m done
          </Button>
          <Button
            variant="contained"
            onClick={continueAssigning}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Yes, continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignOpen}
        onClose={() => {
          if (saving) return;
          setAssignOpen(false);
          // If we already assigned once, force the continue/done choice.
          if (lastAssignSummary && active) setContinuePromptOpen(true);
        }}
        fullWidth
        maxWidth={personal ? 'sm' : 'xs'}
      >
        <DialogTitle>
          {personal ? 'Assign students' : 'Assign batch'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
              {active?.session_title || 'Session'}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 12 }}>
              {inviteTypeLabel(active?.session_type)} · course batches only
            </Typography>
          </Box>

          {!active?.course_id ? (
            <Typography color="error" sx={{ fontSize: 13 }}>
              This session has no course. Recreate it with a course selected.
            </Typography>
          ) : loadingBatches ? (
            <Box sx={{ py: 2, display: 'grid', placeItems: 'center' }}>
              <PulseLoader color="#0d6efd" size={8} />
            </Box>
          ) : batches.length === 0 ? (
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>
              No batches for this course
            </Typography>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel id="assign-batch-label">Batches</InputLabel>
              <Select
                labelId="assign-batch-label"
                multiple
                value={batchIds}
                onChange={handleBatchChange}
                input={<OutlinedInput label="Batches" />}
                renderValue={(selected) =>
                  selected
                    .map((id) => batchLabelById.get(id) || id)
                    .join(', ')
                }
              >
                {batches.map((b) => (
                  <MenuItem key={b.batch_id} value={b.batch_id}>
                    <Checkbox
                      size="small"
                      checked={batchIds.includes(b.batch_id)}
                    />
                    <ListItemText primary={b.batch_name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {personal && batchIds.length > 0 ? (
            <Box
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 1.5,
                maxHeight: 320,
                overflow: 'auto',
              }}
            >
              {loadingStudents ? (
                <Box sx={{ py: 3, display: 'grid', placeItems: 'center' }}>
                  <PulseLoader color="#0d6efd" size={8} />
                </Box>
              ) : students.length === 0 ? (
                <Typography sx={{ p: 2, color: '#64748b', fontSize: 13 }}>
                  No students found in the selected batches.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderBottom: '1px solid #e5e7eb',
                      bgcolor: '#f8fafc',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={
                            selectedStudentIds.length > 0 && !allSelected
                          }
                          onChange={toggleSelectAll}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          Select all ({newlySelectedIds.length}/{newStudentIds.length}
                          {alreadyAssignedIds.length
                            ? ` · ${alreadyAssignedIds.length} already assigned`
                            : ''}
                          )
                        </Typography>
                      }
                    />
                  </Box>
                  <List dense disablePadding>
                    {students.map((student) => {
                      const id = String(student.stuId);
                      const already = alreadyAssignedSet.has(id);
                      const checked = selectedStudentIds.includes(id) || already;
                      return (
                        <ListItem key={id} disablePadding>
                          <ListItemButton
                            onClick={() => toggleStudent(id)}
                            dense
                            disabled={already}
                            sx={already ? { opacity: 0.85, bgcolor: '#f8fafc' } : undefined}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Checkbox
                                edge="start"
                                size="small"
                                checked={checked}
                                disabled={already}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={student.stuname || id}
                              secondary={
                                already
                                  ? `${student.email || id} · Already assigned`
                                  : student.email || undefined
                              }
                              primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                              secondaryTypographyProps={{ fontSize: 11 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </Box>
          ) : null}

          {!personal && batchIds.length > 0 ? (
            <Typography sx={{ color: '#64748b', fontSize: 12 }}>
              All students in the selected{' '}
              {batchIds.length === 1 ? 'batch' : 'batches'} will be invited to this
              group session.
            </Typography>
          ) : null}

          {personal && batchIds.length > 0 ? (
            <Typography sx={{ color: '#64748b', fontSize: 12 }}>
              Only the students you select will be invited to this personal session.
            </Typography>
          ) : null}

          {assignError && (
            <Box
              sx={{
                border: '1px solid #fecaca',
                background: '#fef2f2',
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography color="error" sx={{ fontSize: 13, fontWeight: 600, mb: assignConflicts.length ? 1 : 0, whiteSpace: 'pre-line' }}>
                {assignConflicts.length
                  ? 'Students are not available at this time'
                  : assignError}
              </Typography>
              {assignConflicts.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {assignConflicts.map((row) => (
                    <Box key={row.studentId}>
                      <Typography sx={{ fontSize: 13, color: '#7f1d1d', fontWeight: 600 }}>
                        {row.studentName}
                        {row.studentName !== row.studentId
                          ? ` (${row.studentId})`
                          : ''}
                      </Typography>
                      {row.conflicts.map((c, idx) => (
                        <Typography
                          key={`${row.studentId}-${c.sessionId || idx}`}
                          sx={{ fontSize: 12, color: '#991b1b', pl: 1 }}
                        >
                          Occupied in{' '}
                          <strong>{c.sessionTitle || 'another session'}</strong>
                          {c.sessionStart
                            ? ` · ${c.sessionStart}${c.sessionEnd ? ` → ${c.sessionEnd}` : ''}`
                            : ''}
                          {c.trainer ? ` · trainer ${c.trainer}` : ''}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setAssignOpen(false);
              if (lastAssignSummary && active) setContinuePromptOpen(true);
            }}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleAssign()}
            disabled={!canSave}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {saving
              ? 'Assigning…'
              : personal
                ? `Assign ${newlySelectedIds.length} student${
                    newlySelectedIds.length === 1 ? '' : 's'
                  }`
                : batchIds.length > 1
                  ? `Assign ${batchIds.length} batches`
                  : 'Assign batch'}
          </Button>
        </DialogActions>
      </Dialog>

      {editSession ? (
        <EditSessionModal
          open={editOpen}
          onClose={() => {
            setEditOpen(false);
            setEditSession(null);
          }}
          session={editSession}
          onUpdate={() => {
            void loadSessions();
          }}
        />
      ) : null}

      <ConfirmationModal
        show={cancelTarget != null}
        onHide={() => setCancelTarget(null)}
        onConfirm={() => void performCancelSession()}
        title="Cancel session"
        message={
          cancelTarget
            ? `Cancel "${cancelTarget.session_title || `Session ${cancelTarget.session_id}`}"? It will be removed from scheduled sessions.`
            : ''
        }
        confirmText="Cancel session"
        cancelText="Keep session"
        variant="danger"
        isLoading={cancellingId != null}
      />

      <ConfirmationModal
        show={cancelError != null}
        onHide={() => setCancelError(null)}
        onConfirm={() => setCancelError(null)}
        title="Could not cancel session"
        message={cancelError ?? ''}
        confirmText="OK"
        cancelText=""
        variant="danger"
      />
    </Box>
  );
}
